/**
 * @file ./views/layouts/danfe/producao/mockDadosNfe.js
 *
 * Nota de exemplo no formato **exato** da saída de
 * `converterXmlNfeParaJson` (projeto NFe, `src/leituraNfe/`) — é o contrato
 * de entrada do template de produção.
 *
 * Os dados reproduzem uma NF-e real de saída (venda a prazo, ICMS 00 com
 * alguns itens em substituição tributária) para exercitar o layout com
 * conteúdo verossímil: descrições longas que quebram linha, valores com
 * quatro casas decimais e quantidade de itens suficiente para forçar a
 * paginação com repetição de cabeçalho.
 */

const PRODUTOS_BASE = [
  [
    '00080',
    'QUEIJO RALADO PARMESAO GRANULADO PACOTE 1 KG',
    '04062000',
    'KG',
    41.96,
    18,
  ],
  [
    '00112',
    'MUSSARELA FATIADA RESFRIADA PACOTE 500 G',
    '04061010',
    'KG',
    32.5,
    18,
  ],
  [
    '00145',
    'PRESUNTO COZIDO SEM CAPA DE GORDURA PECA 3,5 KG',
    '16024900',
    'KG',
    27.8,
    18,
  ],
  [
    '00187',
    'REQUEIJAO CREMOSO TRADICIONAL COPO 200 G',
    '04061010',
    'UN',
    6.49,
    18,
  ],
  ['00203', 'MANTEIGA COM SAL EXTRA TABLETE 200 G', '04051000', 'UN', 12.9, 18],
  [
    '00231',
    'LINGUICA TOSCANA SUINA RESFRIADA BANDEJA 1 KG',
    '16010000',
    'KG',
    21.35,
    18,
  ],
  ['00266', 'PEITO DE PERU DEFUMADO FATIADO 200 G', '16023100', 'UN', 14.7, 18],
  [
    '00298',
    'SALAME TIPO ITALIANO PECA APROXIMADA 800 G',
    '16010000',
    'KG',
    58.4,
    18,
  ],
  ['00317', 'IOGURTE NATURAL INTEGRAL POTE 170 G', '04031000', 'UN', 3.25, 12],
  ['00344', 'CREME DE LEITE UHT CAIXA 200 G', '04013000', 'UN', 4.15, 12],
];

/**
 * Repete a base de produtos até formar `quantidade` itens, variando
 * quantidade e valores para o documento não parecer sintético.
 *
 * @param {number} quantidade
 * @returns {Array<Object>} Itens no formato do conversor.
 */
function gerarItens(quantidade) {
  return Array.from({ length: quantidade }, (_, indice) => {
    const [
      codigo,
      descricao,
      ncm,
      unidade,
      valorUnitario,
      aliquota,
    ] = PRODUTOS_BASE[indice % PRODUTOS_BASE.length];

    const quantidadeComercial = Number(((indice % 7) + 1).toFixed(4));
    const valorTotal = Number((quantidadeComercial * valorUnitario).toFixed(2));
    const baseCalculo = valorTotal;
    const valorIcms = Number(((baseCalculo * aliquota) / 100).toFixed(2));

    return {
      numero: String(indice + 1),
      codigo,
      ean: 'SEM GTIN',
      descricao,
      ncm,
      cest: '',
      cfop: '5102',
      unidadeComercial: unidade,
      quantidadeComercial,
      valorUnitarioComercial: valorUnitario,
      valorTotalProduto: valorTotal,
      eanTributavel: 'SEM GTIN',
      unidadeTributavel: unidade,
      quantidadeTributavel: quantidadeComercial,
      valorUnitarioTributavel: valorUnitario,
      valorFrete: 0,
      valorSeguro: 0,
      valorDesconto: 0,
      valorOutrasDespesas: 0,
      indicadorTotal: '1',
      valorAproximadoTributos: Number((valorTotal * 0.1235).toFixed(2)),
      icms: {
        grupo: 'ICMS00',
        origem: '0',
        cst: '00',
        modalidadeBaseCalculo: '3',
        percentualReducaoBase: 0,
        baseCalculo,
        aliquota,
        valor: valorIcms,
        valorFcp: 0,
        baseCalculoSt: 0,
        valorSt: 0,
      },
      ipi: {
        grupo: '',
        enquadramento: '',
        cst: '',
        baseCalculo: 0,
        aliquota: 0,
        valor: 0,
      },
      pis: { grupo: 'PISNT', cst: '06', baseCalculo: 0, aliquota: 0, valor: 0 },
      cofins: {
        grupo: 'COFINSNT',
        cst: '06',
        baseCalculo: 0,
        aliquota: 0,
        valor: 0,
      },
      ibsCbs: {
        cst: '000',
        classificacaoTributaria: '000001',
        baseCalculo: valorTotal,
        ibsUf: { aliquota: 0.1, valor: 0 },
        ibsMunicipio: { aliquota: 0, valor: 0 },
        ibsTotal: 0,
        cbs: { aliquota: 0.9, valor: 0 },
      },
    };
  });
}

/** Quantidade de itens do exemplo — o bastante para gerar duas folhas. */
const QUANTIDADE_ITENS = 34;

const itens = gerarItens(QUANTIDADE_ITENS);

const valorProdutos = Number(
  itens.reduce((total, item) => total + item.valorTotalProduto, 0).toFixed(2),
);
const baseCalculoIcms = valorProdutos;
const valorIcms = Number(
  itens.reduce((total, item) => total + item.icms.valor, 0).toFixed(2),
);
const valorAproximadoTributos = Number(
  itens
    .reduce((total, item) => total + item.valorAproximadoTributos, 0)
    .toFixed(2),
);

/**
 * Nota de exemplo — mesma estrutura de `dadosNfe` de `converterXmlNfeParaJson`.
 * @type {Object}
 */
export const mockDadosNfe = {
  chaveAcesso: '26260737678452000137550000000105991234567890',
  versao: '4.00',
  identificacao: {
    codigoUf: '26',
    codigoNumerico: '12345678',
    naturezaOperacao: 'VENDA DE MERCADORIA ADQUIRIDA DE TERCEIROS',
    modelo: '55',
    serie: '1',
    numero: '10599',
    dataEmissao: '2026-07-29T11:06:00-03:00',
    dataSaidaEntrada: '2026-07-29T14:30:00-03:00',
    tipoOperacao: '1',
    destinoOperacao: '1',
    codigoMunicipioFatoGerador: '2604106',
    tipoImpressao: '1',
    tipoEmissao: '1',
    digitoVerificador: '0',
    tipoAmbiente: '1',
    finalidade: '1',
    indicadorConsumidorFinal: '0',
    indicadorPresenca: '1',
    indicadorIntermediador: '0',
    processoEmissao: '0',
    versaoProcesso: '3.0|NS_API',
    justificativaContingencia: '',
  },
  emitente: {
    cnpj: '37678452000137',
    cpf: '',
    nome: 'CARUARU FRIOS LTDA',
    fantasia: 'CARUARU FRIOS',
    inscricaoEstadual: '089612205',
    inscricaoEstadualSubstitutoTributario: '',
    inscricaoMunicipal: '4471200',
    cnae: '4634601',
    regimeTributario: '3',
    endereco: {
      logradouro: 'AVENIDA BOM PASTOR',
      numero: '621',
      complemento: 'GALPAO A',
      bairro: 'PETROPOLIS',
      codigoMunicipio: '2604106',
      municipio: 'CARUARU',
      uf: 'PE',
      cep: '55030170',
      codigoPais: '1058',
      pais: 'BRASIL',
      telefone: '8199136677',
    },
  },
  destinatario: {
    cnpj: '66191483000167',
    cpf: '',
    idEstrangeiro: '',
    nome: 'MERCADINHO SAO JOSE COMERCIO DE ALIMENTOS EIRELI',
    indicadorInscricaoEstadual: '1',
    inscricaoEstadual: '133127079',
    inscricaoSuframa: '',
    inscricaoMunicipal: '',
    email: 'compras@mercadinhosaojose.com.br',
    endereco: {
      logradouro: 'RUA DOUTOR JOAO PESSOA GUERRA',
      numero: '1450',
      complemento: 'LOJA 02',
      bairro: 'MAURICIO DE NASSAU',
      codigoMunicipio: '2604106',
      municipio: 'CARUARU',
      uf: 'PE',
      cep: '55012290',
      codigoPais: '1058',
      pais: 'BRASIL',
      telefone: '8137213344',
    },
  },
  itens,
  totais: {
    icms: {
      baseCalculo: baseCalculoIcms,
      valor: valorIcms,
      valorDesonerado: 0,
      valorFcp: 0,
      baseCalculoSt: 0,
      valorSt: 0,
      valorFcpSt: 0,
      valorFcpStRetido: 0,
    },
    valorProdutos,
    valorFrete: 0,
    valorSeguro: 0,
    valorDesconto: 0,
    valorImportacao: 0,
    valorIpi: 0,
    valorIpiDevolvido: 0,
    valorPis: 0,
    valorCofins: 0,
    valorOutrasDespesas: 0,
    valorTotalNota: valorProdutos,
    valorAproximadoTributos,
    ibsCbs: {
      baseCalculo: valorProdutos,
      ibs: {
        uf: { diferimento: 0, devolucaoTributo: 0, valor: 0 },
        municipio: { diferimento: 0, devolucaoTributo: 0, valor: 0 },
        valorTotal: 0,
        creditoPresumido: 0,
        creditoPresumidoCondicaoSuspensiva: 0,
      },
      cbs: {
        diferimento: 0,
        devolucaoTributo: 0,
        valor: 0,
        creditoPresumido: 0,
        creditoPresumidoCondicaoSuspensiva: 0,
      },
    },
  },
  transporte: {
    modalidadeFrete: '0',
    quantidadeVolumes: 12,
  },
  pagamento: {
    formas: [
      {
        indicador: '1',
        tipo: '15',
        valor: Number((valorProdutos / 2).toFixed(2)),
      },
      {
        indicador: '1',
        tipo: '17',
        valor: Number((valorProdutos / 2).toFixed(2)),
      },
    ],
    troco: 0,
  },
  informacoesAdicionais: {
    informacoesComplementares:
      'MERCADORIA SUJEITA A CONFERENCIA NO ATO DA ENTREGA. TRANSPORTE REFRIGERADO ENTRE 0 E 4 GRAUS CELSIUS.',
    informacoesFisco:
      'DOCUMENTO EMITIDO POR ME/EPP OPTANTE PELO REGIME NORMAL.',
    observacoes: [
      { campo: 'PEDIDO', texto: '2026-004512' },
      { campo: 'VENDEDOR', texto: 'CARLOS EDUARDO LIMA' },
    ],
  },
  responsavelTecnico: {
    cnpj: '37678452000137',
    contato: 'CAIO EMANUEL',
    email: 'ti@caruarufrios.com.br',
    telefone: '8199136677',
  },
  protocolo: {
    ambiente: '1',
    versaoAplicativo: 'PE-v4.0.9',
    chave: '26260737678452000137550000000105991234567890',
    dataRecebimento: '2026-07-29T11:06:42-03:00',
    numero: '126260009988776',
    status: '100',
    motivo: 'Autorizado o uso da NF-e',
  },
};

/**
 * Variantes do mock para exercitar os cenários do MOC 3.9/3.9.2 nas telas
 * de pré-visualização — cada uma muda só o campo que decide o cenário,
 * mantendo o resto da nota (itens, emitente, destinatário) idêntico.
 */

/** `tpAmb` = 2: tarja e marca d'água de homologação, razão social substituída. */
export const mockDadosNfeHomologacao = {
  ...mockDadosNfe,
  identificacao: { ...mockDadosNfe.identificacao, tipoAmbiente: '2' },
};

/** `tpEmis` = 7 (SVC-RS): aviso "DANFE EM CONTINGÊNCIA" no lugar da consulta, sem marca d'água. */
export const mockDadosNfeContingencia = {
  ...mockDadosNfe,
  identificacao: { ...mockDadosNfe.identificacao, tipoEmissao: '7' },
};

/** Produção sem protocolo (preview antes do retorno da SEFAZ): marca d'água "FALTA PROTOCOLO...". */
export const mockDadosNfeSemProtocolo = {
  ...mockDadosNfe,
  protocolo: { ...mockDadosNfe.protocolo, numero: '', dataRecebimento: '' },
};

export default mockDadosNfe;
